import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useProducts, CatalogSkeleton, LiveProductCard } from "@/components/shop/CatalogProducts";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { useAutoplay } from "@/lib/use-autoplay";
import type { ApiProduct } from "@/lib/api";

/** How long each accessory stays before the strip moves on. */
const ACCESSORY_DELAY_MS = 3500;

const ARROW = "top-[38%] size-10 bg-white shadow-md";

/** The hero's primary button, so "Lihat Semua Produk" is the same dark green, same size. */
const ALL_PRODUCTS_BUTTON = "h-12 px-8 text-[0.9375rem] sm:h-13 sm:text-base";

/**
 * The whole range in one section, two rows deep (Divine, 4 Nov):
 *
 * 1. the desks, then the Paket Hemat bundles;
 * 2. the accessories, moving on their own.
 *
 * Each row shows five cards on a wide screen, so a card never gets too small
 * to read, and the arrows bring in the rest. Narrower screens show fewer; on
 * a phone both rows are swipeable. Every card keeps its "Tambah ke
 * keranjang". One dark green button under the rows opens the catalogue.
 *
 * The section is as wide as the screen allows: Divine asked for no empty
 * margins, as on Omnidesk.
 */
export function Products({ id = "produk" }: { id?: string }) {
  const { products, loading, error } = useProducts();
  // The API lists by sort order, so each line keeps the admin's order.
  const desksAndBundles = [
    ...products.filter((p) => p.line === "desk"),
    ...products.filter((p) => p.line === "package"),
  ];
  const accessories = products.filter((p) => p.line === "accessory");

  return (
    <section id={id} className="mx-auto max-w-[1600px] scroll-mt-20 px-4 py-14 sm:px-6 sm:py-20">
      <h2 className="text-balance text-center text-3xl font-medium tracking-tight text-ink sm:text-4xl">
        Bangun Ruang Kerja Ergonomis Impian Anda
      </h2>

      {loading ? (
        <CatalogSkeleton count={5} className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" />
      ) : error ? (
        <p className="mt-8 text-center" role="alert">
          {error}
        </p>
      ) : !desksAndBundles.length && !accessories.length ? (
        <p className="mt-8 text-center">Belum ada produk tersedia.</p>
      ) : (
        <div className="mt-8 grid gap-6 sm:gap-8">
          {desksAndBundles.length > 0 && <ProductRow id="karya" label="Meja dan Paket Hemat" products={desksAndBundles} />}
          {accessories.length > 0 && (
            <ProductRow id="aksesori" label="Aksesori" products={accessories} autoplayMs={ACCESSORY_DELAY_MS} />
          )}
        </div>
      )}

      <div className="mt-10 flex justify-center sm:mt-12">
        <Button asChild size="lg" className={ALL_PRODUCTS_BUTTON}>
          <Link to="/katalog">
            Lihat Semua Produk
            <ArrowRight />
          </Link>
        </Button>
      </div>
    </section>
  );
}

/**
 * One row of product cards: five across on a wide screen, three on a laptop,
 * two on a tablet, a peek of the next on a phone. Loops, with arrows.
 */
function ProductRow({
  id,
  label,
  products,
  autoplayMs,
}: {
  id: string;
  label: string;
  products: ApiProduct[];
  /** Move on by itself every this many ms. Omit for a row that waits for the arrows. */
  autoplayMs?: number;
}) {
  const { plugins, setApi } = useAutoplay(autoplayMs ?? 0);

  return (
    <div id={id} className="min-w-0 scroll-mt-20">
      <Carousel
        setApi={autoplayMs ? setApi : undefined}
        opts={{ align: "start", loop: true }}
        plugins={autoplayMs ? plugins : undefined}
        aria-label={label}
      >
        <CarouselContent>
          {products.map((p) => (
            <CarouselItem key={p.id} className="flex basis-[74%] sm:basis-1/2 lg:basis-1/3 xl:basis-1/5 [&>*]:w-full">
              <LiveProductCard product={p} />
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className={`left-2 ${ARROW}`} />
        <CarouselNext className={`right-2 ${ARROW}`} />
      </Carousel>
    </div>
  );
}
